import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, forkJoin, map } from 'rxjs';
import { MethodsOfPayment, RecordType } from '@app/enums';
import { IListService } from '@app/services';
import { ProcessMethod } from '../enums/void-payments-process-method.enum';
import { InvoiceCheckSearch, VoidAchs } from '../interfaces/invoice.model';
import { SearchOutstandingCheck, VoidChecks } from '../interfaces/outstandingcheck.model';
import { PaymentEntity, PaymentResultModel } from '../interfaces/payment-entity.model';
import { PaymentParam } from '../interfaces/payment-param.model';

@Injectable()
export class VoidPaymentsService implements IListService {
	endpointBase = '/api/outstandingcheck';
	endpointBaseInvoice = '/api/invoice';

	constructor(private readonly http: HttpClient) {}

	getList(): Observable<any> {
		return EMPTY;
	}

	deleteEntity(): Observable<any> {
		return EMPTY;
	}

	populateList(model: PaymentParam): Observable<PaymentResultModel> {
		if (model.ProcessMethod === ProcessMethod.Ach) {
			return this.getAchList(model);
		}

		return this.getCheckEftList(model);
	}

	protected getCheckEftList(model: PaymentParam): Observable<PaymentResultModel> {
		const params = {
			getProcessed: false,
			getVoided: false,
			getManualEntries: false,
			recordTypes: [RecordType.Check, RecordType.Eft],
			BankId: model.BankId,
			paymentDateFrom: model.PaymentDateFrom.toISOString(),
			paymentDateTo: model.PaymentDateTo.toISOString(),
			ExcludeCcSummary: true,
		} as SearchOutstandingCheck;

		return this.searchOutstandingChecks(params).pipe(
			map((response: any) => {
				const checks = response.isValid
					? response.checks.map(
							(check: any) =>
								({
									...check,
								} as PaymentEntity)
							// eslint-disable-next-line no-mixed-spaces-and-tabs
					  )
					: [];

				const output = {
					isValid: response.isValid,
					errors: response?.errors,
					checks,
				} as PaymentResultModel;

				return output;
			})
		);
	}

	protected getAchList(model: PaymentParam): Observable<PaymentResultModel> {
		const params = {
			fromDate: model.PaymentDateFrom.toISOString(),
			toDate: model.PaymentDateTo.toISOString(),
			methodsOfPayment: [MethodsOfPayment.ACH],
			voided: false,
		} as InvoiceCheckSearch;

		return this.searchInvoiceChecks(params).pipe(
			map((response: any) => {
				const checks = response.isValid
					? response.checks.map(
							(check: any) =>
								({
									...check,
									Id: check.InvoiceCheckId,
									PostDT: check.CheckDT,
									RecordType: 'ACH',
									CheckOrDepositNo: check.CheckNumber,
									PostAmount: check.TotalAmount,
									IsPayrollInvoice: false,
								} as PaymentEntity)
							// eslint-disable-next-line no-mixed-spaces-and-tabs
					  )
					: [];

				const output = {
					isValid: response.isValid,
					errors: response?.errors,
					checks,
				} as PaymentResultModel;

				return output;
			})
		);
	}

	getVcCcFlags(paymentIds: number[]): Observable<{ includesVcCheck: boolean; includesCcCheck: boolean }> {
		const vcChecks$ = this.getVirtualCardChecks(paymentIds).pipe(map((response: any) => response.data));
		const ccChecks$ = this.getCreditCardChecks(paymentIds).pipe(map((response: any) => response.data));

		return forkJoin([vcChecks$, ccChecks$]).pipe(
			map(([vcChecks, ccChecks]) => ({
				includesVcCheck: vcChecks.length > 0,
				includesCcCheck: ccChecks.length > 0,
			}))
		);
	}

	voidPayments(processMethod: ProcessMethod, paymentIds: number[], voidDate: Date, reinstateInvoices: boolean, comment: string): Observable<any> {
		if (processMethod === ProcessMethod.Ach) {
			const voidAchs = {
				InvoiceCheckIds: paymentIds,
				VoidDate: voidDate.toISOString(),
				Comment: comment,
			} as VoidAchs;

			return this.voidAch(voidAchs);
		}

		const voidChecks = {
			CheckIds: paymentIds,
			VoidDate: voidDate.toISOString(),
			ReinstateInvoices: reinstateInvoices,
			Comment: comment,
		} as VoidChecks;

		return this.voidCheck(voidChecks);
	}

	/**
	 * NOTE: This endpoint appears to be used only for voiding payments, so we should determine why
	 * several of the filter settings are not used.
	 */
	searchOutstandingChecks(filter: SearchOutstandingCheck): Observable<any> {
		return this.http.get(`${this.endpointBase}/search`, { params: filter });
	}

	searchInvoiceChecks(filter: InvoiceCheckSearch): Observable<any> {
		return this.http.get(`${this.endpointBaseInvoice}/searchChecks`, { params: filter });
	}

	/**
	 * NOTE: The functionality associated with the IncludesPaymentCardCheck parameter appears to have
	 * been removed from the API, because it is not used in the API code
	 */
	voidCheck(voidChecks: VoidChecks): Observable<any> {
		return this.http.put(`${this.endpointBase}/void`, voidChecks);
	}

	voidAch(voidAchs: VoidAchs): Observable<any> {
		return this.http.put(`${this.endpointBaseInvoice}/void`, voidAchs);
	}

	// TODO: The following 3 endpoints are very inefficient and should be refactored or replaced
	// - It appears that these endpoints are ONLY used for void payments
	// - Although all 3 return check data, we are only checking the number of records returned to set boolean
	//   flags indicating whether any VC/CC checks exist
	// - For all check ids (n), these endpoints execute the same n+1 SQL queries to lookup check and invoice data
	// - Additionally, cardFundSummaryChecks executes another 0 to n SQL queries, one for each invoice found
	// - The cardFundSummaryChecks endpoint could be removed entirely (see note on voidCheck above)
	// - The virtualCardChecks and creditCardChecks endpoints could be combined into a single endpoint, or
	//   eliminated entirely if the endpoint for searchOutstandingChecks is refactored to return VC/CC info with
	//   the check data (such as including the MethodOfPaymentId field from the associated invoice)

	/**
	 * This endpoint was only used to set the IncludesPaymentCardCheck parameter for the VoidChecks object, which
	 * is not used in the API code (see voidCheck comments above). It was left for reference, but we should be able
	 * to remove it from here and the API.
	 */
	getCardFundSummaryChecks(checkIds: number[]): Observable<any> {
		return this.http.get(`${this.endpointBase}/cardFundSummaryChecks`, { params: { ids: checkIds } });
	}

	getVirtualCardChecks(checkIds: number[]): Observable<any> {
		return this.http.get(`${this.endpointBase}/virtualCardChecks`, { params: { ids: checkIds } });
	}

	getCreditCardChecks(checkIds: number[]): Observable<any> {
		return this.http.get(`${this.endpointBase}/creditCardChecks`, { params: { ids: checkIds } });
	}
}
